"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import React from "react";
import { useTranslations } from "next-intl";
import { FaTelegram } from "react-icons/fa6";
import { useAuth } from "@/hooks/use-auth";
import { isBillingEnabled } from "@/lib/billing";
import { Separator } from "../ui";

const BADGES = [
	{
		href: "https://www.producthunt.com/products/projme?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-projme-2",
		img: {
			src: "https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1003917&theme=light&t=1754896632153",
			alt: "forms.projme - An open-source alternative to Google Forms and Typeform | Product Hunt",
			width: 120,
			height: 32,
		},
	},
	{
		href: "https://startupfa.me/s/projme.com?utm_source=forms.projme.ru",
		img: {
			src: "https://startupfa.me/badges/featured/default.webp",
			alt: "Featured on Startup Fame",
			width: 120,
			height: 32,
		},
	},
	{
		href: "https://twelve.tools",
		img: {
			src: "https://twelve.tools/badge3-light.svg",
			alt: "Featured on Twelve Tools",
			width: 120,
			height: 32,
		},
	},
] as const;

const FooterLogo = React.memo(function FooterLogo() {
	return (
		<Link className="inline-flex" href="/">
			<span className="flex items-center gap-3 text-3xl tracking-tight">
				<Image
					alt="forms.projme Logo"
					className="size-10 rounded-lg"
					height={40}
					loading="lazy"
					quality={75}
					src="/android-chrome-192x192.png"
					width={40}
				/>
				<span className="font-dm-sans font-semibold text-foreground">
					forms.projme
				</span>
			</span>
		</Link>
	);
});

const FooterBadges = React.memo(function Footer() {
	return (
		<div className="sr-only flex flex-wrap items-center justify-start gap-3">
			{BADGES.map((badge) => (
				<a
					className="rounded transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-primary"
					href={badge.href}
					key={badge.href}
					rel="noopener noreferrer"
					tabIndex={0}
					target="_blank"
				>
					<img
						alt={badge.img.alt}
						className="h-8 w-auto object-contain"
						height={badge.img.height}
						loading="lazy"
						src={badge.img.src}
						width={badge.img.width}
					/>
				</a>
			))}
		</div>
	);
});

const NavigationLinks = React.memo(function NavigationLinks() {
	const t = useTranslations("footer");
	const { user } = useAuth();
	return (
		<div className="flex flex-col gap-4">
			<h3 className="font-dm-sans font-medium text-foreground">{t("navigation")}</h3>
			<ul className="flex flex-col gap-2">
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/">
						{t("nav.home")}
					</Link>
				</li>
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/#features">
						{t("nav.features")}
					</Link>
				</li>
				{isBillingEnabled() && (
					<li>
						<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/#pricing">
							{t("nav.pricing")}
						</Link>
					</li>
				)}
				{!user && (
					<li>
						<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/login">
							{t("nav.login")}
						</Link>
					</li>
				)}
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/ai-builder">
						{t("nav.aiBuilder")}
					</Link>
				</li>
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/dashboard">
						{t("nav.dashboard")}
					</Link>
				</li>
			</ul>
		</div>
	);
});

const LegalLinks = React.memo(function LegalLinks() {
	const t = useTranslations("footer");
	return (
		<div className="flex flex-col gap-4">
			<h3 className="font-dm-sans font-medium text-foreground">{t("legal")}</h3>
			<ul className="flex flex-col gap-2">
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/legal/privacy">
						{t("legalLinks.privacy")}
					</Link>
				</li>
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/legal/terms">
						{t("legalLinks.terms")}
					</Link>
				</li>
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/legal/gdpr">
						{t("legalLinks.gdpr")}
					</Link>
				</li>
				<li>
					<Link className="text-muted-foreground text-sm transition-colors hover:text-foreground" href="/legal/dpa">
						{t("legalLinks.dpa")}
					</Link>
				</li>
			</ul>
		</div>
	);
});

const ResourcesLinks = React.memo(function ResourcesLinks() {
	const t = useTranslations("footer");
	return (
		<div className="flex flex-col gap-4">
			<h3 className="font-dm-sans font-medium text-foreground">{t("resources")}</h3>
			<ul className="flex flex-col gap-2">
				<li>
					<Link
						className="text-muted-foreground text-sm transition-colors hover:text-foreground"
						href="https://forms.projme.ru/feature-request"
						rel="noopener noreferrer"
						target="_blank"
					>
						{t("resourceLinks.featureRequest")}
					</Link>
				</li>
				<li>
					<Link
						className="text-muted-foreground text-sm transition-colors hover:text-foreground"
						href="https://forms.projme.ru/feedback"
						rel="noopener noreferrer"
						target="_blank"
					>
						{t("resourceLinks.feedback")}
					</Link>
				</li>
				<li>
					<Link
						className="text-muted-foreground text-sm transition-colors hover:text-foreground"
						href="https://forms.projme.ru/bug-report"
						rel="noopener noreferrer"
						target="_blank"
					>
						{t("resourceLinks.bugReport")}
					</Link>
				</li>
			</ul>
		</div>
	);
});

const SocialLinks = React.memo(function SocialLinks() {
	const t = useTranslations("footer");
	return (
		<div className="flex flex-wrap items-center gap-4 text-muted-foreground text-sm">
			<Link
				aria-label={t("social.telegram")}
				className="flex items-center gap-2 transition-colors hover:text-foreground"
				href="https://t.me/projme"
				rel="noopener noreferrer"
				target="_blank"
			>
				<FaTelegram aria-hidden className="size-5" />
			</Link>
			<Link
				aria-label={t("social.email")}
				className="flex items-center gap-2 transition-colors hover:text-foreground"
				href="/email"
				rel="noopener noreferrer"
				target="_blank"
			>
				no-reply@mail.projme.ru
			</Link>
		</div>
	);
});

export default function Footer() {
	const t = useTranslations("footer");
	return (
		<footer className="mx-auto my-12 w-full max-w-7xl bg-background">
			<div className="mx-auto flex w-full flex-col">
				<div>
					<div className="flex flex-col gap-8">
						<div className="flex flex-col gap-8 lg:flex-row lg:justify-between">
							<div className="flex flex-shrink-0 flex-col gap-8">
								<div className="flex flex-shrink-0 flex-col gap-4">
									<FooterLogo />
									<p className="max-w-sm text-muted-foreground text-sm">
										© {new Date().getFullYear()} {t("madeBy")} —{" "}
										<Link
											className="font-medium text-foreground transition-colors hover:underline"
											href="https://t.me/projme"
											rel="noopener noreferrer"
											target="_blank"
										>
											@projme
										</Link>
									</p>
								</div>

								<div className="flex flex-col gap-4">
									<FooterBadges />
								</div>
							</div>
							<Separator className="flex lg:hidden" />

							<div className="flex flex-wrap gap-8 md:grid md:grid-cols-3 md:gap-10">
								<NavigationLinks />
								<LegalLinks />
								<ResourcesLinks />
							</div>
						</div>

						<Separator />

						<div className="flex w-full flex-col items-end justify-center gap-6">
							<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
								<SocialLinks />
							</div>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
}
